import { buildPackingTotals, buildScheduleStops } from "./mapQuoteDetailsToSheet";
import type { MapQuoteDetailsOptions, ShipmentDetailsSheetViewModel } from "./types";

type QuickRequestDetails = {
  organization?: Record<string, unknown>;
  schedule?: Record<string, unknown>;
  settings?: Record<string, unknown>;
};

const mapQuickSpecialRequirements = (requirements: unknown[] | undefined) => {
  if (!Array.isArray(requirements)) return [];
  return requirements.map((entry, index) => {
    const req = entry as Record<string, unknown>;
    const label = String(req.label ?? "");
    return {
      key: req.key != null && req.key !== "" ? String(req.key) : String(index),
      label,
      value: Boolean(req.value),
      ...(req.additional_information ? { additionalInformation: String(req.additional_information) } : {}),
    };
  });
};

const buildQuickOrganisationFields = (
  organization: Record<string, unknown> | undefined,
): Record<string, string | null | undefined> => {
  const creationDate = organization?.quick_request_creation_date as string | undefined;
  const creationTime = organization?.quick_request_creation_time as string | undefined;
  const requestCreationDatetime =
    creationDate && creationTime ? `${creationDate}T${creationTime}` : creationDate ?? creationTime;

  const fields: Record<string, string | null | undefined> = {
    quick_request_number: organization?.quick_request_reference as string | undefined,
    requestor: organization?.quick_request_created_by_name as string | undefined,
    requestor_email: organization?.quick_request_created_by_email as string | undefined,
    request_creation_datetime: requestCreationDatetime,
    invoice_location: organization?.invoice_location as string | undefined,
    cost_center: organization?.cost_center as string | undefined,
    reason_code_group: organization?.reason_code_group as string | undefined,
    reason_code: organization?.reason_code as string | undefined,
    custom_field1: organization?.custom_field1 as string | undefined,
    custom_field2: organization?.custom_field2 as string | undefined,
    custom_field3: organization?.custom_field3 as string | undefined,
    custom_cost_recovery_field1: organization?.custom_cost_recovery_field1 as string | undefined,
    custom_cost_recovery_field2: organization?.custom_cost_recovery_field2 as string | undefined,
  };

  if (organization?.cost_recovery_required !== undefined && organization?.cost_recovery_required !== null) {
    fields.cost_recovery_required = organization.cost_recovery_required ? "Yes" : "No";
  }

  return fields;
};

export const mapQuickDetailsToSheet = (
  details: QuickRequestDetails,
  options: MapQuoteDetailsOptions,
): ShipmentDetailsSheetViewModel => {
  const { organization, schedule } = details;
  const goodItems = Array.isArray(organization?.good_items) ? (organization.good_items as unknown[]) : [];
  const goodsConfirmationType = organization?.goods_confirmation_type as string | undefined;

  return {
    organisation: {
      title: (organization?.quick_request_title as string) ?? "",
      shipperName: (organization?.shipper as string) ?? "",
      ...((organization?.shipper_logo as string)
        ? { shipperLogo: organization.shipper_logo as string }
        : {}),
      paymentLocation: (organization?.payment_location as string) ?? "",
      paymentLocationReference: organization?.payment_location_reference as string | undefined,
      hideInPrint: Boolean(details.settings?.hide_organizational_data),
      additionalComments: (organization?.additional_comments as string | null) ?? null,
      longField: (organization?.custom_longfield1 as string | null) ?? null,
      fields: buildQuickOrganisationFields(organization),
    },
    service: {
      incoterm: organization?.incoterm as string | undefined,
      incoterm_instructions: organization?.incoterm_instructions as string | undefined,
      freightModes: [],
      specialRequirements: mapQuickSpecialRequirements(organization?.special_requirements as unknown[] | undefined),
    },
    providers: null,
    schedule: {
      stops: buildScheduleStops(schedule),
      ...(schedule?.multistop_instructions
        ? { multistopInstructions: String(schedule.multistop_instructions) }
        : {}),
      ...(schedule?.consignee !== undefined ? { consignee: schedule.consignee } : {}),
      ...(schedule?.notify_party !== undefined ? { notifyParty: schedule.notify_party } : {}),
      clearance: {},
    },
    packing: {
      goodItems,
      totals: buildPackingTotals(goodItems, organization),
      goodsEstimated: goodsConfirmationType === "Estimated",
      goodsConfirmationType: goodsConfirmationType ?? null,
      requestedCbm: (organization?.requested_cbm as number | null | undefined) ?? null,
    },
    shipmentId: options.shipmentId ?? (organization?.quick_request_reference as string) ?? "",
  };
};
